import React, { useState } from 'react';
import { Card, Badge, AIPanel, ProgressBar, DataRow } from '@/components';
import { Plus, CheckCircle2, Clock, AlertCircle } from 'lucide-react';

const CarePlan = () => {
  const [showAddMenu, setShowAddMenu] = useState(false);

  // Provider view data
  const goals = [
    {
      id: 1,
      name: 'HbA1c < 7%',
      current: 8.2,
      target: 7.0,
      unit: '%',
      progress: 65,
      dueDate: '2026-06-30',
    },
    {
      id: 2,
      name: 'BP < 130/80',
      current: '138/88',
      target: '130/80',
      unit: 'mmHg',
      progress: 72,
      dueDate: '2026-05-15',
    },
    {
      id: 3,
      name: 'Lose 10 lbs',
      current: 185,
      target: 175,
      unit: 'lbs',
      progress: 40,
      dueDate: '2026-08-30',
    },
  ];

  const orders = [
    {
      id: 1,
      type: 'Medication',
      name: 'Semaglutide (GLP-1 agonist)',
      status: 'Sent',
      statusColor: 'bg-blue-500/20 text-blue-400',
      icon: '💊',
    },
    {
      id: 2,
      type: 'Lab',
      name: 'HbA1c + Comprehensive Metabolic Panel',
      status: 'Ordered',
      statusColor: 'bg-amber-500/20 text-amber-400',
      icon: '🧪',
    },
    {
      id: 3,
      type: 'Referral',
      name: 'Ophthalmology (Eye Exam)',
      status: 'Urgent',
      statusColor: 'bg-red-500/20 text-red-400',
      icon: '👁️',
    },
    {
      id: 4,
      type: 'Referral',
      name: 'Registered Dietitian',
      status: 'Pending',
      statusColor: 'bg-gray-500/20 text-gray-400',
      icon: '🥗',
    },
    {
      id: 5,
      type: 'Appointment',
      name: 'Follow-up Telehealth Visit',
      status: 'Scheduled',
      statusColor: 'bg-green-500/20 text-green-400',
      icon: '📹',
    },
  ];

  // Patient view data
  const plainLanguagePlan = [
    'Work with your doctor to understand diabetes and how to manage it daily',
    'Take your new diabetes medication (Semaglutide) as prescribed and track any side effects',
    'Check your blood sugar levels regularly and keep a log to share with your healthcare team',
    'Schedule an eye doctor appointment to check for diabetes-related eye problems',
    'Meet with a dietitian to create a meal plan that works for you and supports weight loss',
  ];

  const aiReferralLetter = `Dear Colleague,

I am writing to refer Maria Garcia, a 54-year-old patient, for specialized care as part of her comprehensive diabetes management plan.

Clinical Context:
Ms. Garcia presents with Type 2 Diabetes Mellitus with recent HbA1c of 8.2%, hypertension (BP 138/88), and elevated BMI of 32. She has been compliant with her current regimen but requires intensification of therapy and specialized evaluation for microvascular complications.

Recommended Actions:
• Ophthalmology: Urgent evaluation for diabetic retinopathy screening
• Dietetics: Medical nutrition therapy for diabetes and weight management
• Continued medical management: Initiation of GLP-1 agonist (Semaglutide) for glycemic control and weight reduction

The patient is motivated and engaged in her care. Please coordinate with our office for any findings and recommendations.

Best regards,
Dr. Priya Reddy
Smart on FHIR Clinic`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0b0d14] to-[#0f1119] p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white mb-2">Care Plan</h1>
          <p className="text-gray-400">Maria Garcia • Type 2 Diabetes • Created Mar 21, 2026</p>
        </div>

        {/* Main Content - Split Layout */}
        <div className="grid grid-cols-2 gap-6">
          {/* Left Column - Provider View */}
          <div className="space-y-6">
            {/* Goals Section */}
            <Card className="p-6">
              <h2 className="text-xl font-semibold text-white mb-6">Clinical Goals</h2>

              <div className="space-y-6">
                {goals.map((goal) => (
                  <div key={goal.id} className="pb-6 border-b border-[#1e2235] last:pb-0 last:border-0">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className="font-semibold text-gray-100">{goal.name}</p>
                        <p className="text-xs text-gray-500 mt-1">Due: {goal.dueDate}</p>
                      </div>
                      <span className="text-xs font-mono bg-blue-500/20 text-blue-400 px-2 py-1 rounded">
                        {goal.progress}%
                      </span>
                    </div>
                    <ProgressBar value={goal.progress} color="bg-blue-500" />
                    <p className="text-sm text-gray-400 mt-3">
                      Current: <span className="font-semibold text-gray-200">{goal.current} {goal.unit}</span>
                      {' → '}
                      Target: <span className="font-semibold text-green-400">{goal.target} {goal.unit}</span>
                    </p>
                  </div>
                ))}
              </div>
            </Card>

            {/* Orders & Referrals Section */}
            <Card className="p-6">
              <h2 className="text-xl font-semibold text-white mb-6">Orders & Referrals</h2>

              <div className="space-y-3">
                {orders.map((order) => (
                  <div
                    key={order.id}
                    className="p-4 bg-[#12141e] rounded-lg border border-[#1e2235] hover:border-[#252d3d] transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-grow">
                        <span className="text-xl mt-1">{order.icon}</span>
                        <div className="min-w-0">
                          <p className="font-medium text-gray-100">{order.name}</p>
                          <p className="text-xs text-gray-500 mt-1">{order.type}</p>
                        </div>
                      </div>
                      <Badge className={`${order.statusColor} whitespace-nowrap`}>{order.status}</Badge>
                    </div>
                  </div>
                ))}
              </div>

              {/* Action Button */}
              <div className="relative mt-6 pt-6 border-t border-[#1e2235]">
                <button
                  onClick={() => setShowAddMenu(!showAddMenu)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
                >
                  <Plus className="w-5 h-5" />
                  Add Goal / Order / Referral
                </button>

                {showAddMenu && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-[#12141e] border border-[#1e2235] rounded-lg overflow-hidden z-10 shadow-lg">
                    <button className="w-full px-4 py-3 text-left text-gray-100 hover:bg-[#1e2235] transition-colors flex items-center gap-2">
                      <Plus className="w-4 h-4 text-blue-400" />
                      Add Clinical Goal
                    </button>
                    <button className="w-full px-4 py-3 text-left text-gray-100 hover:bg-[#1e2235] transition-colors flex items-center gap-2 border-t border-[#1e2235]">
                      <Plus className="w-4 h-4 text-amber-400" />
                      Add Order
                    </button>
                    <button className="w-full px-4 py-3 text-left text-gray-100 hover:bg-[#1e2235] transition-colors flex items-center gap-2 border-t border-[#1e2235]">
                      <Plus className="w-4 h-4 text-purple-400" />
                      Add Referral
                    </button>
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* Right Column - Patient View */}
          <div className="space-y-6">
            {/* Plain Language Care Plan */}
            <Card className="p-6">
              <h2 className="text-xl font-semibold text-white mb-6">Your Care Plan</h2>
              <p className="text-sm text-gray-400 mb-6">Here's what we've planned for your health:</p>

              <div className="space-y-4">
                {plainLanguagePlan.map((step, idx) => (
                  <div key={idx} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-500/20 text-blue-400 font-semibold text-sm">
                        {idx + 1}
                      </span>
                    </div>
                    <div className="flex-grow pt-1">
                      <p className="text-gray-200 text-sm leading-relaxed">{step}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* AI-Generated Referral Letter */}
            <AIPanel
              title="AI-Generated Referral Letter"
              content={
                <div className="space-y-3">
                  <div className="text-xs text-gray-400 bg-[#12141e] p-3 rounded border border-[#1e2235] max-h-48 overflow-y-auto font-mono leading-relaxed">
                    {aiReferralLetter.split('\n').map((line, idx) => (
                      <div key={idx}>{line}</div>
                    ))}
                  </div>
                  <div className="flex gap-2 pt-2">
                    <button className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors text-sm">
                      Copy
                    </button>
                    <button className="flex-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors text-sm">
                      Send to Specialist
                    </button>
                  </div>
                </div>
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarePlan;
